import * as XLSX from "xlsx";

export interface FocusValidatorConfig {
  sheetSelector?: "auto" | string[];
  ignoreEmptySheets?: boolean;
  deleteEmptyRows?: boolean;
  returnCleanedWorkbook?: boolean;
  headerMustContain?: string[];
  skipHeaders?: string[];
}

export interface ValidationIssue {
  sheet: string;
  client: string;
  period: string;
  metric: string;
  columnDisplayName: string;
  cellAddress: string;
  rowNumber: number;
  colNumber: number;
  message: string;
  empId: string;
  empName: string;
}

export interface ValidationResult {
  errors: string[];
  issues: ValidationIssue[];
  issueTable: any[][];
  cleanedWorkbook?: ArrayBuffer;
  cleanedAoABySheet?: Record<string, any[][]>;
}

type AoARow = {
  excelRowIndex: number; // original Excel row (0‑based)
  cells: any[];
};

export async function validateFocusAreaWorkbook(
  file: File,
  config?: FocusValidatorConfig
): Promise<ValidationResult> {

  const cfg = {
    sheetSelector: config?.sheetSelector ?? "auto",
    ignoreEmptySheets: config?.ignoreEmptySheets ?? true,
    deleteEmptyRows: config?.deleteEmptyRows ?? true,
    returnCleanedWorkbook: config?.returnCleanedWorkbook ?? false,
    headerMustContain: (config?.headerMustContain ?? ["FC Client", "Emp ID"]).map(s => s.toLowerCase()),
    skipHeaders: (config?.skipHeaders ?? []).map(s => s.trim().toLowerCase()),
  };

  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });

  const targetSheets =
    cfg.sheetSelector === "auto"
      ? wb.SheetNames
      : cfg.sheetSelector ?? wb.SheetNames;

  const errors: string[] = [];
  const issues: ValidationIssue[] = [];
  const cleanedAoABySheet: Record<string, any[][]> = {};

  function getMergedContinuationRows(ws: XLSX.WorkSheet): Set<number> {
    const rows = new Set<number>();
    for (const m of ws["!merges"] ?? []) {
      for (let r = m.s.r + 1; r <= m.e.r; r++) {
        rows.add(r);
      }
    }
    return rows;
  }

  for (const sheetName of targetSheets) {
    const ws = wb.Sheets[sheetName];
    if (!ws) continue;

    const rawAoA = XLSX.utils.sheet_to_json<any[]>(ws, {
      header: 1,
      blankrows: true,
      defval: null,
    });

    let aoa: AoARow[] = rawAoA.map((row, idx) => ({
      excelRowIndex: idx,
      cells: row,
    }));

    if (cfg.ignoreEmptySheets && isSheetCompletelyEmpty(aoa.map(r => r.cells))) {
      cleanedAoABySheet[sheetName] = aoa.map(r => r.cells);
      continue;
    }

    const headerIdx = findHeaderRowIndexByContains(
      aoa.map(r => r.cells),
      cfg.headerMustContain
    );
    if (headerIdx < 0) continue;

    const headerRow = aoa[headerIdx].cells;
    const range = XLSX.utils.decode_range(ws["!ref"] as string);

    // ✅ detect first real Excel data column (handles blank column A)
    let excelColOffset = range.s.c;
    for (let i = 0; i < headerRow.length; i++) {
      if (toStr(headerRow[i]) !== "") {
        excelColOffset = range.s.c + i;
        break;
      }
    }

    const usedMaxCol0 = inferUsedMaxColFromHeader(headerRow, range.e.c);
    const colMeta = buildFocusColumnMeta({
      headerRow,
      usedMaxCol0Based: usedMaxCol0,
      monthStarts: [],
      monthLabels: [],
      totalFteBCol: -1,
      skipHeaders: cfg.skipHeaders,
    });

    const mergedContinuationRows = getMergedContinuationRows(ws);

    // ✅ remove merged continuation + spacer rows
    if (cfg.deleteEmptyRows) {
      aoa = aoa.filter((r, idx) => {
        if (idx <= headerIdx) return true;
        return !mergedContinuationRows.has(r.excelRowIndex);
      });

      if (cfg.returnCleanedWorkbook) {
        wb.Sheets[sheetName] = XLSX.utils.aoa_to_sheet(aoa.map(r => r.cells));
      }
    }

    cleanedAoABySheet[sheetName] = aoa.map(r => r.cells);

    const fcClientCol = findFirstCol(headerRow, "FC Client");

    for (const aoaRow of aoa) {
    // ✅ Skip header and above (Excel-based)
    if (aoaRow.excelRowIndex <= headerIdx) continue;

    const row = aoaRow.cells;
    const excelRow = aoaRow.excelRowIndex;

    if (isRowCompletelyEmpty(row, usedMaxCol0)) continue;

    const client = fcClientCol >= 0 ? toStr(row[fcClientCol]) : "(unknown client)";

      for (let c = 0; c <= usedMaxCol0; c++) {
        const meta = colMeta[c];
        if (!meta || meta.skip) continue;

        if (isEmpty(row[c])) {
          const excelCol = c + excelColOffset;

          const cellAddress = XLSX.utils.encode_cell({
            r: excelRow,      // ✅ REAL Excel row
            c: excelCol,      // ✅ REAL Excel column
          });

          const empIdCol = findFirstCol(headerRow, "Emp ID");
          const empNameCol = findFirstCol(headerRow, "Emp Name");

          const issue: ValidationIssue = {
            sheet: sheetName,
            client,
            period: meta.period,
            metric: meta.metric,
            columnDisplayName: meta.display,
            cellAddress,
            rowNumber: excelRow + 1,  // ✅ Excel row number
            colNumber: excelCol + 1,  // ✅ Excel column number
            message: `Client "${client}" missing "${meta.display}" at ${cellAddress}.`,
            empId: empIdCol >= 0 ? toStr(row[empIdCol]) : "",
            empName: empNameCol >= 0 ? toStr(row[empNameCol]) : ""

          };

          issues.push(issue);
          errors.push(issue.message);
        }
      }
    }
  }

  // const issueTable = [["Client", "Emp ID", "Emp Name", "Missing Column", "Sheet", "Period", "Cell"]];
  const issueTable = [["Client", "Emp ID", "Emp Name", "Missing Column", "Sheet", "Period"]];
  for (const i of issues) {
    // issueTable.push([i.client, i.empId, i.empName, i.columnDisplayName, i.sheet, i.period, i.cellAddress]);
    issueTable.push([i.client, i.empId, i.empName, i.columnDisplayName, i.sheet, i.period]);
  }

  return {
    errors,
    issues,
    issueTable,
    cleanedAoABySheet,
    cleanedWorkbook: cfg.returnCleanedWorkbook
      ? XLSX.write(wb, { bookType: "xlsx", type: "array" })
      : undefined,
  };
}

/* ------------------ helpers (unchanged) ------------------ */

function toStr(v: any): string {
  return v == null ? "" : String(v).trim();
}

function isEmpty(val: any): boolean {
  return val == null || (typeof val === "string" && val.trim() === "");
}

function isSheetCompletelyEmpty(aoa: any[][]): boolean {
  return aoa.every(row => row.every(cell => isEmpty(cell)));
}

function isRowCompletelyEmpty(row: any[], maxCol: number): boolean {
  for (let c = 0; c <= maxCol; c++) {
    if (!isEmpty(row[c])) return false;
  }
  return true;
}

function findHeaderRowIndexByContains(aoa: any[][], mustContainLower: string[]): number {
  for (let r = 0; r < Math.min(100, aoa.length); r++) {
    const rowLower = aoa[r].map(v => toStr(v).toLowerCase());
    if (mustContainLower.every(h => rowLower.includes(h))) return r;
  }
  return -1;
}

function inferUsedMaxColFromHeader(headerRow: any[], fallback: number): number {
  for (let c = headerRow.length - 1; c >= 0; c--) {
    if (toStr(headerRow[c])) return c;
  }
  return fallback;
}

function findFirstCol(headerRow: any[], label: string): number {
  const t = label.toLowerCase();
  return headerRow.findIndex(h => toStr(h).toLowerCase() === t);
}

type FocusMeta = {
  period: string;
  metric: string;
  display: string;
  skip: boolean;
  kind: "base";
};

function buildFocusColumnMeta(params: {
  headerRow: any[];
  usedMaxCol0Based: number;
  monthStarts: number[];
  monthLabels: string[];
  totalFteBCol: number;
  skipHeaders: string[];
}): Record<number, FocusMeta> {

  const meta: Record<number, FocusMeta> = {};

  for (let c = 0; c <= params.usedMaxCol0Based; c++) {
    const raw = toStr(params.headerRow[c]);
    if (!raw) continue;

    meta[c] = {
      period: "",
      metric: raw,
      display: raw,
      skip: params.skipHeaders.includes(raw.toLowerCase()),
      kind: "base",
    };
  }
  return meta;
}